import { useId, useRef, useState } from 'react';
import { message } from '@sl/core';
import { Button } from '../../ui/Button/Button';
import { Skeleton, SkeletonGroup } from '../../ui/Skeleton/Skeleton';
import { VisuallyHidden } from '../../ui/VisuallyHidden/VisuallyHidden';
import { cx } from '../../ui/cx';
import { messageText } from '../messages';
import { useShell } from '../shell/shellContext';
import { testId } from '../testId';
import { PERIOD_COPY, compareHint, triggerHint } from './copy';
import { LazyPeriodPopover, usePeriodPopoverPrefetch } from './lazyPopover';
import { usePeriod } from './periodContext';
import styles from './PeriodControl.module.css';
import { useLocation } from 'react-router';

// What sits in the top bar's right-hand slot (T-036 reserved it): the date-range button and the
// "Compare to previous" switch, exactly as `_shared/web-shell.html` has them. The search field joins them
// later; the give-way order for all three is in `AppShell.module.css` (DESIGN_SYSTEM §10).
//
// The button is the popover's trigger (`aria-haspopup="dialog"`, `aria-expanded`), so Escape closes it and
// focus comes back here (TEST_PLAN §1.6 K6). The switch is a real `role="switch"`: it flips one global thing,
// and a menu would be a worse answer than a switch for an on/off.

export function PeriodControl() {
  const { account, messages, deviceTimeZone } = useShell();
  const period = usePeriod();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  // T-321: the panel is its own chunk now (`lazyPopover.tsx`) — warmed on idle so a press never waits for a
  // round trip, and never on the critical path (BUG-155).
  usePeriodPopoverPrefetch();

  // Nothing in the URL and the saved range still on its way: show the shape of the control rather than a
  // range the user never chose, which would then change under them (REQ-021 AC4).
  if (period.settling) {
    return (
      <SkeletonGroup
        label={PERIOD_COPY.loading}
        slowAfterMs={0}
        className={styles.control}
        testID={testId('period', 'loading')}
      >
        <Skeleton shape="pill" width={190} height={32} />
        <Skeleton shape="pill" width={160} height={32} />
      </SkeletonGroup>
    );
  }

  /** REQ-021 AC6 — the name slot is carrying the served day / month rather than the preset's name. */
  const nameIsDay = period.chipName !== period.label;
  /** …and on a single-day preset that name IS the range, so the dates beside it would say it twice. */
  const repeatsName = nameIsDay && period.chipName === period.dates;

  return (
    <div className={styles.control}>
      <Button
        ref={triggerRef}
        variant="secondary"
        icon="calendar"
        trailingIcon="chevron-down"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        title={triggerHint(account.timezone)}
        testID={testId('shell', 'period', 'trigger')}
        onClick={() => setOpen((was) => !was)}
      >
        {/* `shell.period` is the chip's NAME slot — the preset's name ("Last 7 days"), and the DS-02 reading
            of it is exact. **REQ-021 AC6 (D-704): where the preset's name is a claim about which day and the
            served range is not the day the device's own clock would give it, this reads the served day or
            month instead** ("Sep 30", "September"), because the name is what the owner asked for and the chip
            is what they are being shown. The preset ROW in the menu keeps the name; the id, the URL and the
            saved preference are untouched, and on a demo account nothing changes (D-626). The dates and the
            "so far" flag are siblings, never inside it; the button itself is `shell.period.trigger`. Mobile
            made the same split (T-053's parity note). */}
        <span
          data-testid={testId('shell', 'period')}
          data-period-name={nameIsDay ? 'day' : undefined}
        >
          {period.chipName}
        </span>
        {/* D-589: the dates are the SERVER's echo whenever it differs from the ask (`meta.period`, API §12.1),
            so what the button says is what is on screen. `data-period-source` is how QA tells the two apart;
            the hint names the range that was asked for, and the freshness label says why it moved.
            AC6's one visual consequence: on `today` / `yesterday` the name slot above is that same single
            day, so the dates would print it twice — the element stays, with its exact text, and the repeat is
            hidden from the eye and from the accessibility tree. */}
        <span
          className={cx(styles.dates, repeatsName && styles.datesRepeat)}
          data-testid={testId('shell', 'period', 'dates')}
          data-period-source={period.served === null ? undefined : 'server'}
          aria-hidden={repeatsName ? 'true' : undefined}
          title={period.servedHint ?? undefined}
        >
          {period.dates}
        </span>
        {period.resolved.includesToday ? (
          /* REQ-021 AC6 — the word stays and its explainer changes: on a day-old pull nothing is "still
             coming in", so "so far" means "still filling up when we last heard from Meta" (D-704).
             **T-211: where it does mean what it says, it says it in the catalogue's own words.** REQ-283 AC3
             spells the flag "so far — still coming in" and `state.today.so_far` is that sentence; the chip
             has room for two words and the rest rides in the title, which is exactly how w17 §7 draws this
             tag in its day table (`title="So far — still coming in"`). Nothing new is written: the visible
             word is `PERIOD_COPY.soFar` as before, and the hover is a key that already existed and had no
             reader. */
          <span
            className={styles.soFar}
            title={period.soFarExplainer ?? messageText(message('state.today.so_far'), messages)}
          >
            {PERIOD_COPY.soFar}
          </span>
        ) : null}
      </Button>

      <button
        type="button"
        role="switch"
        aria-checked={period.view.compare}
        className={cx(styles.compare, period.view.compare && styles.compareOn)}
        title={period.view.compare ? compareHint(period.compareDates) : PERIOD_COPY.compareHintOff}
        data-testid={testId('shell', 'compare')}
        onClick={() => period.setCompare(!period.view.compare)}
      >
        <span className={styles.compareTrack} aria-hidden="true">
          <span className={styles.compareThumb} />
        </span>
        <span className={styles.compareLabel}>{PERIOD_COPY.compare}</span>
        {/* REQ-021 AC3 states the comparison period whether or not the comparison is on: off, the dates
            stay on screen (muted, saying which period WOULD be compared) but leave the switch's
            accessible name, which then reads "Compare to previous · Not compared with anything". */}
        <span
          className={cx(styles.compareDates, !period.view.compare && styles.compareDatesOff)}
          data-testid={testId('shell', 'compare', 'dates')}
          aria-hidden={period.view.compare ? undefined : 'true'}
        >
          {period.compareDates}
        </span>
        {period.view.compare ? null : <VisuallyHidden>{PERIOD_COPY.compareOffNote}</VisuallyHidden>}
      </button>

      <LazyPeriodPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        id={popoverId}
        account={account}
        messages={messages}
        deviceTimeZone={deviceTimeZone}
      />
    </div>
  );
}

/**
 * BUG-128 / D-495: a screen suppresses the period controls it does not govern. The alert detail's figures are
 * the alert's own window, so the date range and the compare switch would be a control over nothing there.
 */
export const PERIOD_FREE_ROUTES: readonly RegExp[] = [/^\/alerts\/(?!rules(?:\/|$))[^/]+\/?$/];

/** The period control, absent on a route that does not govern a period (`PERIOD_FREE_ROUTES`). */
export function PeriodControlWhenGoverned() {
  const { pathname } = useLocation();
  return PERIOD_FREE_ROUTES.some((route) => route.test(pathname)) ? null : <PeriodControl />;
}
